import {
  Button,
  DateRangePicker,
  Icon,
  IconButton,
  InputTypes,
  SliderModal,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { FinanceEnumsFilterTypeEnum } from '@genial/services/dist/models';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Text, Box, ScrollView, Flex } from 'native-base';
import React, { useState } from 'react';
import { Dimensions } from 'react-native';
import { useRecoilValue } from 'recoil';

import { NavigationRouteProp } from '../../../../../../../routes';
import { useStatementsActions } from '../../../../../../../store/customerDetail/stataments/actions';
import {
  statementOptionQueryState,
  statementsFilterParamsState,
} from '../../../../../../../store/customerDetail/stataments/atom';
import { StatementOptionQuery } from '../../../../../../../store/customerDetail/stataments/types';
import { SelectableItem } from '../../../../../../NewApplication/components/SelectableItem';

import {
  extractOptionsValues,
  extractValueSelected,
  flexStyle,
  textButtonStyle,
  SelectableOptionsProps,
} from './constants';

type ParamsProps = { params: { options: SelectableOptionsProps[] } };

const { height } = Dimensions.get('window');

export const StatementsFilter: React.FC = () => {
  const currentStatementsFilterParams = useRecoilValue(statementsFilterParamsState);
  const currentStatementOptionQuery = useRecoilValue(statementOptionQueryState);

  const { filterType, startDate, endDate } = currentStatementsFilterParams;

  const { params } = useRoute<NavigationRouteProp>() as ParamsProps;
  const { goBack } = useNavigation();

  const { updateStatementOption, updateStatementsFilterParams } = useStatementsActions();

  const initialValuesSelected = extractOptionsValues(
    params?.options,
    filterType,
    currentStatementOptionQuery
  );

  const initialValuesStarted = extractOptionsValues(params?.options);

  const [selectedValues, setSelectedValues] = useState(initialValuesSelected || []);
  const [initialDate, setInitialDate] = useState(startDate);
  const [finalDate, setFinalDate] = useState(endDate);

  const buttonCleanFiltersIsDisabled =
    JSON.stringify(initialValuesStarted) === JSON.stringify(selectedValues);

  const buttonApplyFiltersIsDisabled =
    JSON.stringify(initialValuesSelected) === JSON.stringify(selectedValues);

  const handleApplyFilters = () => {
    const filterTypeSelected = extractValueSelected(
      selectedValues,
      'Período'
    ) as FinanceEnumsFilterTypeEnum;
    const transactionTypeSelected = extractValueSelected(
      selectedValues,
      'Tipo'
    ) as StatementOptionQuery;

    if (filterTypeSelected && filterTypeSelected !== filterType) {
      const dates = { startDate: initialDate, endDate: finalDate };

      updateStatementsFilterParams({
        filterType: filterTypeSelected,
        ...(filterTypeSelected === 'Other' ? dates : {}),
      });
    }

    if (transactionTypeSelected && transactionTypeSelected !== currentStatementOptionQuery) {
      updateStatementOption(transactionTypeSelected);
    }
    goBack();
  };

  const handleCleanFilters = () => {
    setSelectedValues(initialValuesStarted);
    updateStatementsFilterParams({
      filterType: 'LastSevenDays',
      startDate: undefined,
      endDate: undefined,
    });
    setFinalDate(undefined);
    setInitialDate(undefined);
    updateStatementOption('current-account');
  };

  const handleClickPeriodInputDate = () => {
    const valueSelectable = ['Período:Outros:Other'];
    setSelectedValues(valueSelectable);
  };

  const handleSelectDates = (initialDateValue: Date, finalDateValue: Date) => {
    const valueSelectable = ['Período:Outros:Other'];
    setSelectedValues(valueSelectable);
    setInitialDate(initialDateValue);
    setFinalDate(finalDateValue);
  };

  const DateRangePickerInputComponentProps = {
    ...((filterType !== 'Other'
      ? { onClick: handleClickPeriodInputDate }
      : {}) as InputTypes.Props),
  };

  return (
    <SliderModal dismissWhenClickOutside onDismiss={goBack} direction={'bottom'}>
      <VStack maxHeight={{ base: height * 0.8, lg: 'full' }} flex={1}>
        <VStack
          alignItems="center"
          w="full"
          p={Spacing['inline.xxs']}
          borderTopRadius={Spacing['inline.xxxs']}
          backgroundColor={Color['neutral.white']}
          space={Spacing['inline.xxxs']}>
          <Box w="55px" background="neutral.lightest" borderRadius={BoxRadius.sm} h={1} />
          <HStack
            w={'full'}
            space={Spacing['stack.quarck']}
            alignItems="center"
            justifyContent="space-between">
            <Text variant="semibold" fontSize={Typography['fontsize.lg']}>
              Escolha os filtros de pesquisa
            </Text>
            <IconButton
              icon="Close"
              testId={'button-close'}
              iconColor={Color['neutral.dark']}
              iconSize="xxs"
              onPress={goBack}
            />
          </HStack>
        </VStack>
        <ScrollView showsVerticalScrollIndicator={false} backgroundColor={Color['neutral.white']}>
          <VStack px={Spacing['inline.xxs']} maxWidth={'366px'} space={Spacing['stack.xxxs']}>
            {params?.options?.map((value) => (
              <VStack key={value.title.modal} space={Spacing['stack.xxxs']}>
                <Text variant="semibold" fontSize={Typography['fontsize.md']}>
                  {value.title.modal}
                </Text>
                <Flex style={flexStyle} direction="row" flexWrap="wrap">
                  {value?.items?.map((option) => {
                    const valueSelectable = `${value.title.modal}:${option.label}:${option.value}`;
                    const isSelected = selectedValues.includes(valueSelectable);

                    const handleSelect = () => {
                      setSelectedValues((state) => {
                        if (isSelected) {
                          return state.filter((item) => item !== valueSelectable);
                        } else {
                          const newState = state.filter(
                            (item) => !item.startsWith(`${value.title.modal}:`)
                          );
                          newState.push(valueSelectable);
                          return newState;
                        }
                      });
                      if (option.value !== 'Other') {
                        setInitialDate(undefined);
                        setFinalDate(undefined);
                      }
                    };

                    const selectItemIsDisabled =
                      selectedValues.some((str) => str.toLowerCase().includes('direct-treasury')) &&
                      value.title.modal === 'Período';

                    return (
                      <SelectableItem
                        key={valueSelectable}
                        isSelected={selectedValues.includes(valueSelectable)}
                        label={option.label}
                        isDisabled={selectItemIsDisabled}
                        onPress={handleSelect}
                      />
                    );
                  })}
                </Flex>
              </VStack>
            ))}
          </VStack>
          <VStack space={Spacing['stack.xxxs']} w={'100%'} p={Spacing['inline.xxs']}>
            <Text variant="regular" fontSize={Typography['fontsize.md']}>
              Filtre por data:
            </Text>
            <DateRangePicker
              onClear={() => {}}
              forceMobile
              initialDate={initialDate}
              finalDate={finalDate}
              placeholder="Data atual"
              onSelectDates={handleSelectDates}
              calendarPosition="left"
              inputComponentProps={DateRangePickerInputComponentProps}
            />
          </VStack>
        </ScrollView>
        <VStack
          backgroundColor={Color['neutral.white']}
          alignItems={'center'}
          justifyContent={'space-between'}
          p={Spacing['inline.xxs']}
          space={Spacing['inline.nano']}
          borderTopWidth={1}
          borderTopColor={Color['neutral.lightest']}
          w="full">
          <Button
            variant={'textLink'}
            isDisabled={buttonCleanFiltersIsDisabled}
            w="full"
            onPress={handleCleanFilters}
            _text={textButtonStyle}
            rightIcon={
              <Icon
                testID="remove-filter-icon"
                name="Trash"
                color={
                  Color[buttonCleanFiltersIsDisabled ? 'neutral.light' : 'feedback.error.default']
                }
                size="xxs"
              />
            }>
            Remover Filtros
          </Button>
          <Button
            w="full"
            variant={'primary'}
            onPress={handleApplyFilters}
            _text={textButtonStyle}
            isDisabled={buttonApplyFiltersIsDisabled}>
            Aplicar filtros
          </Button>
        </VStack>
      </VStack>
    </SliderModal>
  );
};
